import { normalizeWord } from '../../lib/words'
import { splitWords } from '../../lib/wordNotes'

// ─── The shared dictionary: the panel's arithmetic (V2-p) ────────────────────

export const DICTIONARY_STATUSES = ['draft', 'needs_review', 'published']

export const DICTIONARY_STATUS_FA = {
  draft: 'پیش‌نویس',
  needs_review: 'نیازمند بازبینی',
  published: 'منتشرشده',
}

export const DICTIONARY_STATUS_CLS = {
  draft: 'bg-amber-100 text-amber-800',
  needs_review: 'bg-sky-100 text-sky-800',
  published: 'bg-green-100 text-green-800',
}

/**
 * The links of one sentence as the panel keeps them: `{ index, entryId,
 * headword }[]`, the shape `AdminSentenceDto.wordLinks` arrives in. `headword`
 * is for the chip; only `index` and `entryId` go back on the wire.
 */
export function linksFromSentence(sentence) {
  return (sentence?.wordLinks ?? []).map(({ index, entryId, headword }) => ({ index, entryId, headword }))
}

/** The wire shape of one sentence's `wordLinks`, sorted by index. */
export function linksToWire(links) {
  return (links ?? [])
    .map(({ index, entryId }) => ({ index, entryId }))
    .sort((a, b) => a.index - b.index)
}

/**
 * Drops links that no longer match once the admin has edited `textEn`: past
 * the last word, or on a word that no longer normalizes to the entry's
 * headword. The server would refuse or drop these anyway
 * (`invalid_word_links` / `droppedLinks`); doing it here keeps the chips
 * honest while the admin types.
 */
export function pruneWordLinks(links, textEn) {
  const words = splitWords(textEn).map(normalizeWord)
  return (links ?? []).filter(link => {
    const word = words[link.index]
    if (word === undefined || word === '') return false
    return normalizeWord(link.headword ?? '') === word
  })
}

/** Set (or replace) the link at `index`; `entry` null removes it. */
export function withLink(links, index, entry) {
  const rest = (links ?? []).filter(link => link.index !== index)
  if (!entry) return rest
  return [...rest, { index, entryId: entry.id, headword: entry.headword }].sort((a, b) => a.index - b.index)
}

/** The headword a new entry gets from a tapped token: the normalized word, so "Coffee," becomes "coffee". */
export function headwordForToken(token) {
  return normalizeWord(token)
}

/** The warning after `admin-lessons_update` dropped links; '' when none were. */
export function droppedLinksMessage(dropped) {
  if (!dropped || dropped.length === 0) return ''
  const items = dropped.map(d => `«${d.headword}» (جملهٔ ${d.sentenceIndex + 1}، کلمهٔ ${d.index + 1})`)
  return `با تغییر متن جمله، این پیوندهای واژه‌نامه برداشته شدند: ${items.join('، ')}. اگر هنوز لازم‌اند، دوباره وصلشان کنید.`
}

/** The dictionary's own refusals in Persian; null for anything else (errorMessage takes over). */
export function dictionaryRefusal(error) {
  const code = error?.response?.data?.error
  switch (code) {
    case 'conflict':
      return 'برای این کلمه از قبل یک مدخل هست؛ همان را جست‌وجو و ویرایش کنید.'
    case 'entry_in_use':
      return 'این مدخل به جمله‌هایی وصل است و نمی‌شود کلمه‌اش را عوض کرد؛ برای کلمهٔ تازه یک مدخل تازه بسازید.'
    case 'entry_incomplete':
      return 'برای انتشار، دست‌کم یکی از معنی، ترجمه یا توضیح را پر کنید.'
    case 'invalid_headword':
      return 'این کلمه حرف یا رقمی ندارد و نمی‌تواند مدخل باشد.'
    case 'invalid_word_links':
      return 'یکی از پیوندهای واژه‌نامه با متن جمله نمی‌خواند؛ کلمه را دوباره وصل کنید.'
    case 'audio_rejected':
      return 'فایل پذیرفته نشد: خالی، بیش از حد بزرگ، یا با فرمت پشتیبانی‌نشده.'
    case 'audio_unreadable':
      return 'مدت فایل قابل خواندن نبود؛ فایل صوتی دیگری امتحان کنید.'
    default:
      return null
  }
}

/** Whether an entry has anything a learner could read — what publishing needs. */
export function hasPersianContent(entry) {
  return [entry?.meaningFa, entry?.translationFa, entry?.explanationFa].some(v => (v ?? '').trim().length > 0)
}
